import React from 'react';
import { useTranslation } from 'react-i18next';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, IconButton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import QVContainer from '../../common/ui/qvshop/components/QVContainer';
import ShopConfig from '../../common/api/config/shopConfig';
import { htmlDecode } from '../../common/utils/HtmlDecode';

const Thumbnail = styled(Box)(({ theme }) => ({
    width: 56,
    height: 56,
    borderRadius: theme.shape.borderRadius,
    border: '1px solid ' + theme.palette.divider,
    backgroundColor: theme.palette.action.hover,
    flexShrink: 0,
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}));

function ComponentThumbnail({ image, name }) {
    if(!image || image === '_dummy.svg')
    {
        return <Thumbnail />;
    }

    return (
        <Thumbnail
            component="img"
            src={ShopConfig.config().getURLForProductImage(image)}
            alt={htmlDecode(name)}
            sx={{ objectFit: 'cover' }}
        />
    );
}

function SpecificationsTooltip({ specifications }) {
    const { t } = useTranslation();

    if(!specifications || specifications.length === 0)
    {
        return (
            <Box sx={{ p: 1.5 }}>
                <Typography variant="body2">
                    {t('productdetails.set_components.no_specifications')}
                </Typography>
            </Box>
        );
    }

    return (
        <TableContainer sx={{ maxWidth: 360 }}>
            <Table size="small" aria-label={t('productdetails.set_components.specifications')}>
                <TableBody>
                    {specifications.map((specification, index) => (
                        <TableRow key={index + '_' + specification.attribute + '_' + specification.value}>
                            <TableCell component="th" scope="row" sx={{ fontWeight: 600, borderBottom: 0, whiteSpace: 'nowrap' }}>
                                {htmlDecode(specification.attribute)}
                            </TableCell>
                            <TableCell sx={{ borderBottom: 0 }}>
                                {htmlDecode(specification.value)}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

export default function SetComponentsBox({ components }) {
    const { t } = useTranslation();

    if(!components || components.length === 0)
    {
        return null;
    }

    return (
        <QVContainer padding={0} borderRadius={8} sx={{ width: '100%', maxWidth: '100%', flexDirection: 'column', overflow: 'hidden' }}>
            <TableContainer sx={{ maxWidth: '100%' }}>
                <Table
                    size="small"
                    aria-label={t('productdetails.set_components')}
                    sx={{
                        width: '100%',
                        '& tbody tr:last-of-type td': {
                            borderBottom: 0,
                        },
                    }}
                >
                    <TableHead>
                        <TableRow sx={{ height: 48 }}>
                            <TableCell sx={{ width: 72, py: 1.5, verticalAlign: 'middle' }}>{t('productdetails.set_components.thumbnail')}</TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap', py: 1.5, verticalAlign: 'middle' }}>{t('productdetails.set_components.component')}</TableCell>
                            <TableCell align="right" sx={{ width: 88, py: 1.5, verticalAlign: 'middle' }}>{t('productdetails.set_components.quantity')}</TableCell>
                            <TableCell align="center" sx={{ width: 56, py: 1.5, verticalAlign: 'middle' }}>{t('productdetails.set_components.specifications')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {components.map((component) => (
                            <TableRow key={component.id}>
                                <TableCell>
                                    <ComponentThumbnail image={component.image} name={component.component_article_name} />
                                </TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                    <Stack direction="column" spacing={0.25}>
                                        <Typography variant="body2" fontWeight={600}>
                                            {htmlDecode(component.component_article_name)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {htmlDecode(component.component_article_number)}
                                        </Typography>
                                    </Stack>
                                </TableCell>
                                <TableCell align="right">
                                    {component.quantity}
                                </TableCell>
                                <TableCell align="center">
                                    <Tooltip
                                        placement="left"
                                        title={<SpecificationsTooltip specifications={component.attributes} />}
                                        componentsProps={{
                                            tooltip: {
                                                sx: {
                                                    bgcolor: 'background.paper',
                                                    color: 'text.primary',
                                                    border: 1,
                                                    borderColor: 'divider',
                                                    boxShadow: 2,
                                                    maxWidth: 380,
                                                }
                                            }
                                        }}
                                    >
                                        <IconButton size="small" aria-label={t('productdetails.set_components.specifications_for', { component: htmlDecode(component.component_article_name) })}>
                                            <InfoOutlinedIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </QVContainer>
    );
}
